import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowRight, Sparkles, Gift, CreditCard, Plane, GraduationCap, ShieldCheck, Smartphone, Package, Wallet, BadgeCheck, Zap, Globe } from 'lucide-react';
import { DemoModal } from '@/components/DemoModal';
import { ChatWidget } from '@/components/ChatWidget';
import MegaFooter from '@/components/MegaFooter';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';

const LOGO_URL = "/logo.svg";




const WhatsAppFloat = () => (
  <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="whatsapp-float">
    <MessageCircle size={28} color="white" />
  </a>
);

// Reward Category Card
const RewardCard = ({ icon: Icon, color, title, description, items, link, linkLabel }) => (
  <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-4 feature-icon ${color}`}>
      <Icon size={28} color="white" />
    </div>
    <h3 className="text-gray-900 font-bold text-lg mb-2">{title}</h3>
    <p className="text-gray-600 text-sm mb-4">{description}</p>
    <div className="flex flex-wrap gap-2">
      {items.map((item, i) => (
        <span key={i} className="px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-600">{item}</span>
      ))}
    </div>
    {link && (
      <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-[#2C39B8] hover:text-[#7CC326] transition-colors" data-testid={"reward-card-link-" + title.toLowerCase().replace(/\s+/g, '-')}>
        {linkLabel || 'Explore catalog'} <ArrowRight size={14} />
      </a>
    )}
  </div>
);

// Stats
const StatCard = ({ value, label }) => (
  <div className="text-center">
    <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-[#2C39B8] to-[#6FC8F5] bg-clip-text text-transparent mb-2">{value}</div>
    <div className="text-gray-600">{label}</div>
  </div>
);

export default function RewardsPage() {
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  return (
    <div data-testid="rewards-page">
      <Navbar onDemoClick={() => setDemoModalOpen(true)} />
      <SEOHead title="Rewards Catalog - 500+ Brands, Instant UPI, Travel & Merchandise" description="ChannelLoyalty rewards catalog: Instant UPI cash payouts, 500+ brand gift vouchers (Amazon, Flipkart), travel rewards, merchandise, insurance benefits, and training credits for channel partners." keywords="instant UPI rewards, dealer cash payouts, channel partner gift vouchers, loyalty travel rewards, merchandise incentives" canonical="https://channelloyalty.ai/rewards" />
      
      <main>
        {/* Banner Hero */}
        <section className="hero-banner-full">
          <div className="hero-banner-carousel">
            <div className="hero-banner-track">
              <div className="hero-banner-slide active">
                <img src="/images/banner_rewards.jpg" alt="ChannelLoyalty Rewards - Rewards That Partners Actually Want" />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="section-light section">
          <div className="container">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <StatCard value="$2B+" label="Rewards Distributed" />
              <StatCard value="50+" label="Reward Categories" />
              <StatCard value="10K+" label="Brand Partners" />
              <StatCard value="<2s" label="Instant Payout" />
            </div>
          </div>
        </section>

        {/* Reward Categories */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <span className="section-badge">Reward Categories</span>
              <h2>Every Reward Your Partners Want</h2>
              <p>Comprehensive catalog designed for B2B channel preferences.</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <RewardCard
                icon={Wallet}
                color="#2C39B8"
                title="Instant Cash & UPI"
                description="Real-time payouts directly to partner bank accounts or UPI."
                items={["UPI Transfer", "Bank Transfer", "Paytm", "PhonePe"]}
              />
              <RewardCard
                icon={CreditCard}
                color="pink"
                title="Gift Vouchers"
                description="Digital vouchers from 500+ leading brands across categories."
                items={["Amazon", "Flipkart", "Big Bazaar", "Croma", "Myntra"]}
              />
              <RewardCard
                icon={Plane}
                color="cyan"
                title="Travel & Holidays"
                description="Flight bookings, hotel stays, and curated travel packages."
                items={["Flights", "Hotels", "Holiday Packages", "Pilgrimages"]}
              />
              <RewardCard
                icon={Package}
                color="orange"
                title="Physical Goods"
                description="Electronics, appliances, and merchandise delivered to doorstep."
                items={["Electronics", "Appliances", "Tools", "Merchandise"]}
                link="https://loyltykart.com"
                linkLabel="Explore on LoyltYKart"
              />
              <RewardCard
                icon={ShieldCheck}
                color="green"
                title="Insurance & Protection"
                description="Health, life, and accidental insurance for partners and families."
                items={["Health Insurance", "Life Cover", "Accident Cover", "Family Plans"]}
              />
              <RewardCard
                icon={GraduationCap}
                color="blue"
                title="Education & Skills"
                description="Training certifications, courses, and skill development programs."
                items={["Certifications", "Online Courses", "Skill Training", "Workshops"]}
              />
            </div>
          </div>
        </section>

        {/* Payout Infrastructure */}
        <section className="section-dark section">
          <div className="container">
            <div className="section-header">
              <span className="section-badge" style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.2)', color: 'white' }}>Payout Rails</span>
              <h2 className="text-white">Enterprise-Grade Payout Infrastructure</h2>
              <p className="text-gray-400">KYC-verified, compliance-ready, audit-trailed payouts at scale.</p>
            </div>
            
            <div className="grid md:grid-cols-4 gap-6 mt-12">
              <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-center">
                <Zap size={32} className="text-blue-400 mx-auto mb-4" />
                <h4 className="text-white font-bold mb-2">Instant Payouts</h4>
                <p className="text-gray-400 text-sm">Real-time disbursement to any payment method</p>
              </div>
              <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-center">
                <BadgeCheck size={32} className="text-green-400 mx-auto mb-4" />
                <h4 className="text-white font-bold mb-2">KYC Verified</h4>
                <p className="text-gray-400 text-sm">Aadhaar, PAN, and bank verification built-in</p>
              </div>
              <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-center">
                <ShieldCheck size={32} className="text-cyan-400 mx-auto mb-4" />
                <h4 className="text-white font-bold mb-2">Compliance Ready</h4>
                <p className="text-gray-400 text-sm">TDS deduction, GST compliance, audit trails</p>
              </div>
              <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-center">
                <Globe size={32} className="text-pink-400 mx-auto mb-4" />
                <h4 className="text-white font-bold mb-2">Multi-Country</h4>
                <p className="text-gray-400 text-sm">Support for 12+ countries and currencies</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="cta-section">
          <div className="container relative z-10">
            <h2>Ready to Reward Your Channel Partners?</h2>
            <p>See how ChannelLoyalty&apos;s rewards catalog can drive engagement and loyalty.</p>
            <div className="cta-buttons">
              <button onClick={() => setDemoModalOpen(true)} className="btn btn-primary">
                Explore Rewards <ArrowRight size={18} />
              </button>
              <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="btn btn-ghost">
                <MessageCircle size={18} /> Talk to Expert
              </a>
            </div>
          </div>
        </section>
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
      <DemoModal isOpen={demoModalOpen} onClose={() => setDemoModalOpen(false)} />
    </div>
  );
}
